import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { useContext, useEffect, useState } from "react";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { convertFileUrlToFileName, zodKeys } from "@/lib/utils";
import { FaPaperclip } from "react-icons/fa6";
import {
  CustomSiteSettings,
  customSiteSettingsApi,
} from "@/api/customSiteSettings";
import { useReadApi } from "@/hooks/useReadApi";
import { HexColorPicker } from "react-colorful";
import Loader from "@/components/ui/loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";
// import { v4 as uuidv4 } from "uuid";
import { Progress } from "@/components/ui/progress";

const FormSchema = z.object({
  title: z.string().min(3, "title should be minimum 3 characters long."),
  support_email: z.string().optional().nullable(),
  primary_color: z.string().optional().nullable(),
  secondary_color: z.string().optional().nullable(),
  date_format: z.string().optional().nullable(),
  logo: z.instanceof(File).or(z.string()).nullable().optional(),
});

const PlanColors = {
  Gold: "#FFD700",
  Silver: "#C0C0C0",
  Bronze: "#CD7F32",
  black: "#000000",
};

export function CustomSiteSettingsPage() {
  const { values: contextValues } = useContext(AuthContext) as AuthContextType;
  const { user, planDetails, tenantMetrics } = contextValues;
  const tenantOCR = contextValues.planDetails?.tenant_ocr;
  const tenantAISummaryUsage =
    contextValues.planDetails?.tenant_ai_summary_usage;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const {
    loading: loadingResult,
    apiResult: existingCustomSiteSetting,
    refresh,
  } = useReadApi<CustomSiteSettings>(customSiteSettingsApi, "first");

  const errorHandler = useApiErrorHandler("CustomSiteSettings", {});

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      title: "",
      support_email: null,
      primary_color: null,
      secondary_color: null,
      date_format: null,
      logo: null,
    },
  });

  const storagePercentage = useCallback(() => {
    const maxAllowedSize = tenantMetrics?.allocated_size_mb || 1;
    const usedSize = tenantMetrics?.used_size_mb || 0;

    const percentage = Math.ceil((usedSize / maxAllowedSize) * 100);
    if (percentage >= 100) {
      return { percentage: 100, maxAllowedSize, usedSize };
    }
    return { percentage, maxAllowedSize, usedSize };
  }, [tenantMetrics]);

  const ocrPercentage = useCallback(() => {
    if (!tenantOCR) {
      return { percentage: 0, maxPagesAllowed: 0, pagesUsed: 0 };
    }
    const maxPagesAllowed = tenantOCR?.max_pages_allowed || 1;
    const pagesUsed = tenantOCR?.pages_used || 0;

    const percentage = Math.ceil((pagesUsed / maxPagesAllowed) * 100);
    if (percentage >= 100) {
      return { percentage: 100, maxPagesAllowed, pagesUsed };
    }
    return { percentage, maxPagesAllowed, pagesUsed };
  }, [tenantOCR]);

  const aiSummaryUsagePercentage = useCallback(() => {
    if (!tenantAISummaryUsage) {
      return { percentage: 0, maxPagesAllowed: 0, pagesUsed: 0 };
    }
    const maxPagesAllowed = tenantAISummaryUsage?.max_pages_allowed || 1;
    const pagesUsed = tenantAISummaryUsage?.pages_used || 0;

    const percentage = Math.ceil((pagesUsed / maxPagesAllowed) * 100);
    if (percentage >= 100) {
      return { percentage: 100, maxPagesAllowed, pagesUsed };
    }
    return { percentage, maxPagesAllowed, pagesUsed };
  }, [tenantAISummaryUsage]);

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      setLoading(true);
      if (existingCustomSiteSetting && existingCustomSiteSetting.id) {
        await customSiteSettingsApi
          .update({
            id: existingCustomSiteSetting.id.toString() + "/",
            headers: {
              "content-type": "multipart/form-data",
            },
            data: {
              title: data.title,
              primary_color: data.primary_color,
              secondary_color: data.secondary_color,
              date_format: data.date_format,
              support_email: data.support_email,
              ...(typeof data.logo === "string" ? {} : { logo: data.logo }),
              updated_by: user?.user.id,
            },
            token: user?.access,
          })
          .then(() => {
            toast({
              title: t("custom_site_settings.toast.updated"),
            });
          });
      } else {
        await customSiteSettingsApi
          .create({
            url: "/",
            headers: {
              "content-type": "multipart/form-data",
            },
            data: {
              title: data.title,
              primary_color: data.primary_color,
              secondary_color: data.secondary_color,
              date_format: data.date_format,
              support_email: data.support_email,
              ...(typeof data.logo === "string" ? {} : { logo: data.logo }),
              created_by: user?.user.id,
            },
            token: user?.access,
          })
          .then(() => {
            toast({
              title: t("custom_site_settings.toast.added"),
            });
          });
      }
      await refresh();
      window.location.reload();
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (existingCustomSiteSetting) {
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...existingCustomSiteSetting,
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCustomSiteSetting, user]);

  const {
    percentage: storagePercentageValue,
    maxAllowedSize,
    usedSize,
  } = storagePercentage();

  const {
    percentage: ocrPercentageValue,
    maxPagesAllowed,
    pagesUsed,
  } = ocrPercentage();

  const {
    percentage: aiSummaryUsagePercentageValue,
    maxPagesAllowed: aiSummaryUsageMaxPagesAllowed,
    pagesUsed: aiSummaryUsagePagesUsed,
  } = aiSummaryUsagePercentage();

  const buttonAction = existingCustomSiteSetting?.id ? "Update" : "Add";
  const planColor = PlanColors[
    (planDetails?.tenant_plan.plan_name || "black") as keyof typeof PlanColors
  ] as unknown as string;

  return (
    <>
      <div className="container">
        {loadingResult && <Loader size={24} />}
        <h1 className="text-3xl mb-8">
          {t("custom_site_settings.label.site_settings")}
        </h1>
        <div className="flex flex-col gap-2 mb-4 p-4 border">
          <h3 className="text-xl">
            {t("custom_site_settings.label.subscription_details")}
          </h3>
          <hr />
          <ul>
            <li>
              {t("custom_site_settings.label.plan")}
              {": "}
              <span
                style={{
                  color: planColor,
                }}
              >
                <b>{planDetails?.tenant_plan.plan_name}</b>
              </span>
            </li>
            <li>
              {t("custom_site_settings.label.total_user_limit")}
              {": "}
              <b>{planDetails?.tenant_plan.user_limit}</b>
            </li>
            <li>
              {t(
                "custom_site_settings.label.has_access_to_scientific_research",
              )}
              {": "}
              <b>{planDetails?.tenant_plan.research_access ? "✅" : "❌"}</b>
            </li>
            <li>
              {t(
                "custom_site_settings.label.has_access_to_ai_summary_generation",
              )}
              {": "}
              <b>
                {planDetails?.tenant_plan.has_ai_summary_access ? "✅" : "❌"}
              </b>
            </li>
            <li className="my-2">
              <p className="mb-1">
                {t("custom_site_settings.label.storage_utilization")}
                {": "} <b>{`${storagePercentageValue}%`}</b>
                <b className="mx-4">
                  ({usedSize}/{maxAllowedSize}{" "}
                  {t("custom_site_settings.label.megabytes")})
                </b>
              </p>
              <Progress
                value={storagePercentageValue}
                max={100}
                className="w-[50%]"
              />
            </li>
            <li className="my-2">
              <p className="mb-1">
                {t("custom_site_settings.label.ocr_utilization")}
                {": "} <b>{`${ocrPercentageValue}%`}</b>
                <b className="mx-4">
                  ({pagesUsed}/{maxPagesAllowed}{" "}
                  {t("custom_site_settings.label.pages")})
                </b>
              </p>
              <Progress
                value={ocrPercentageValue}
                max={100}
                className="w-[50%]"
              />
            </li>
            <li className="my-2">
              <p className="mb-1">
                {t("custom_site_settings.label.ai_summary_utilization")}
                {": "} <b>{`${aiSummaryUsagePercentageValue}%`}</b>
                <b className="mx-4">
                  ({aiSummaryUsagePagesUsed}/{aiSummaryUsageMaxPagesAllowed}{" "}
                  {t("custom_site_settings.label.pages")})
                </b>
              </p>
              <Progress
                value={aiSummaryUsagePercentageValue}
                max={100}
                className="w-[50%]"
              />
            </li>
          </ul>
        </div>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-[full] md:w-[60%] flex flex-col gap-8"
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("custom_site_settings.label.site_title")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        "custom_site_settings.placeholder.site_title",
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="support_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("custom_site_settings.label.support_email")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        "custom_site_settings.placeholder.support_email",
                      )}
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>
                    {t("custom_site_settings.description.support_email")}
                  </FormDescription>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="logo"
              render={({ field }) => {
                let fileName = null;
                if (typeof field.value === "string") {
                  fileName = convertFileUrlToFileName(field.value);
                }
                return (
                  <FormItem>
                    <FormLabel>
                      {t("custom_site_settings.label.logo")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        accept=".png, .jpg, .jpeg"
                        type="file"
                        {...field}
                        value={undefined}
                        onChange={(event) =>
                          form.setValue(
                            "logo",
                            event.target.files ? event.target.files[0] : null,
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {fileName && (
                      <>
                        <div className="flex gap-2 items-center">
                          <FaPaperclip color="skyblue" />
                          <a
                            href={field.value as string}
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-primary"
                          >
                            {fileName}
                          </a>
                          <Button
                            type="button"
                            onClick={() => {
                              form.setValue("logo", new File([], ""));
                            }}
                          >
                            {t("common.statictext.remove_file")}
                          </Button>
                        </div>
                        <img
                          src={field.value?.toString()}
                          className="w-auto h-[150px]"
                          alt={t("custom_site_settings.label.logo")}
                        />
                      </>
                    )}
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="date_format"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("custom_site_settings.label.date_format")}
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || undefined}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "custom_site_settings.placeholder.date_format",
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem
                        value={t(
                          "custom_site_settings.date_format_options.MM/dd/yyyy",
                        )}
                      >
                        {t(
                          "custom_site_settings.date_format_options.MM/dd/yyyy",
                        )}
                      </SelectItem>
                      <SelectItem
                        value={t(
                          "custom_site_settings.date_format_options.yyyy-MM-dd",
                        )}
                      >
                        {t(
                          "custom_site_settings.date_format_options.yyyy-MM-dd",
                        )}
                      </SelectItem>
                      <SelectItem
                        value={t(
                          "custom_site_settings.date_format_options.dd-MM-yyyy",
                        )}
                      >
                        {t(
                          "custom_site_settings.date_format_options.dd-MM-yyyy",
                        )}
                      </SelectItem>
                      <SelectItem
                        value={t(
                          "custom_site_settings.date_format_options.MM-dd-yyyy",
                        )}
                      >
                        {t(
                          "custom_site_settings.date_format_options.MM-dd-yyyy",
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {t("custom_site_settings.description.date_format")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="primary_color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("custom_site_settings.label.primary_color")}
                  </FormLabel>
                  <FormControl className="!mb-4">
                    <HexColorPicker
                      color={field.value || "#ffffff"}
                      onChange={(value: string) => {
                        form.setValue("primary_color", value);
                      }}
                    />
                  </FormControl>
                  <FormLabel>
                    {t("custom_site_settings.label.color_preview")}
                  </FormLabel>
                  <FormDescription className="!mb-4 flex flex-wrap gap-2">
                    <div
                      className="rounded h-16 w-16 border border-black"
                      style={{
                        backgroundColor: field.value || "#ffffff",
                      }}
                    />
                    <Button
                      variant="destructive"
                      type="button"
                      onClick={() => {
                        form.setValue("primary_color", "#E47C41");
                      }}
                    >
                      {t("common.statictext.reset")}
                    </Button>
                    <p className="w-full">
                      {t("custom_site_settings.description.primary_color")}
                    </p>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="secondary_color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("custom_site_settings.label.secondary_color")}
                  </FormLabel>
                  <FormControl className="!mb-4">
                    <HexColorPicker
                      color={field.value || "#ffffff"}
                      onChange={(value: string) => {
                        form.setValue("secondary_color", value);
                      }}
                    />
                  </FormControl>
                  <FormLabel>
                    {t("custom_site_settings.label.color_preview")}
                  </FormLabel>
                  <FormDescription className="!mb-4 flex flex-wrap gap-2">
                    <div
                      className="rounded h-16 w-16 border border-black"
                      style={{
                        backgroundColor: field.value || "#ffffff",
                      }}
                    />
                    <Button
                      variant="destructive"
                      type="button"
                      onClick={() => {
                        form.setValue("secondary_color", "#F5F5F5");
                      }}
                    >
                      {t("common.statictext.reset")}
                    </Button>
                    <p className="w-full">
                      {t("custom_site_settings.description.secondary_color")}
                    </p>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button disabled={loading} type="submit">
              {loading ? "Loading..." : buttonAction}
            </Button>
          </form>
        </Form>
      </div>
    </>
  );
}
